import React, { useState, useMemo } from 'react';
import '../OrderConfirmation.css';

interface Tax {
  type: string;
  subTaxType: string;
  rate: number;
  value: number;
}

interface DiscountEntry {
  name: string;
  value: number;
  issueDiscountType: string;
  DiscountMetal: string;
  crossmetal?: boolean;
  cashbackType?: string;
}

interface BreakDownSummary {
  originalQuantity: number;
  adjustedQuantity: number;
  unitPrice: number;
  lineTaxAmount: number;
  lineAmount: number;
  lineDiscount?: number;
  lineCashback?: number;
  lineTotal: number;
  roundingDifference: number;
}

interface ItemBreakDown {
  metalCode: string;
  name: string;
  discount?: DiscountEntry[];
  taxes: Tax[];
  summary: BreakDownSummary;
}

interface ItemSummary {
  itemTotalTax: number;
  itemTotalDiscount: number;
  itemTotalCashback?: number;
  totalLineAmount: number;
  itemTotalAmount: number;
}

interface QuoteItem {
  itemBreakDown: ItemBreakDown[];
  summary: ItemSummary;
}

interface QuoteSummary {
  itemTotalTax: number;
  itemTotalDiscount: number;
  itemTotalCashback?: number;
  quotationTotal: number;
  quotationTax: number;
  quotationRoundingDifference: number;
}

interface QuoteData {
  ebQuoteRef: string;
  type: 'SELL' | 'BUYBACK';
  items: QuoteItem[];
  summary: QuoteSummary;
}

interface PropsType {
  handleModal: (val: boolean) => void;
  isShow: boolean;
  data: QuoteData | null;
  onProceedPayment: () => void;
  minutes: number;
  seconds: number;
  OrderPayMethod?: Array<{ paymentMethod: string; amount: number }>;
}

const METAL_META: Record<string, { label: string; colorClass: string }> = {
  Au: { label: 'Gold', colorClass: 'Au' },
  Ag: { label: 'Silver', colorClass: 'Ag' },
  Pt: { label: 'Platinum', colorClass: 'Pt' },
  Pd: { label: 'Palladium', colorClass: 'Pd' },
};

const fmt = (n: number, decimals = 2) =>
  n.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const fmtCompact = (n: number) =>
  '₹ ' + Math.round(n).toLocaleString('en-IN');

const InfoIcon = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="#92400E" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: 1 }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ── Discount classifier — shared by single- and multi-metal rendering.
// cashbackType is checked FIRST: issueDiscountType is just the discount
// MECHANISM (e.g. "AMOUNT", "RATE", "PRODUCT_QTY") and does not by itself
// tell us whether an entry is cashback — confirmed via real API responses
// where a cashback coupon carried issueDiscountType "AMOUNT" in one case
// and "RATE" in another. cashbackType is the only reliable discriminator.
const classifyDiscount = (bd?: ItemBreakDown) => {
  const entry = bd?.discount?.[0];
  if (!entry) return null;

  if (entry.cashbackType === 'CASHBACK') {
    return { kind: 'CASHBACK' as const, entry };
  }
  if (entry.issueDiscountType === 'PRODUCT_QTY') {
    return { kind: 'PRODUCT_QTY' as const, entry };
  }
  return { kind: 'MONETARY' as const, entry };
};

const OrderConfirmation: React.FC<PropsType> = ({
  handleModal,
  isShow,
  data,
  onProceedPayment,
  minutes,
  seconds,
  OrderPayMethod,
}) => {

  const primaryMetalIndex = useMemo(() => {
    if (!data) return 0;
    return data.items.reduce(
      (maxIdx, item, idx, arr) =>
        item.summary.itemTotalAmount > arr[maxIdx].summary.itemTotalAmount ? idx : maxIdx,
      0
    );
  }, [data]);

  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>(() => ({
    [primaryMetalIndex]: false,
  }));

  if (!data || !isShow) return null;

  const isBuyback = data.type === 'BUYBACK';
  const isMultiple = data.items.length > 1;

  const toggleCard = (idx: number) =>
    setExpandedCards((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Consolidated totals for multi-metal
  const totalLineAmount = data.items.reduce((s, i) => s + i.summary.totalLineAmount, 0);
  const totalTax = data.items.reduce((s, i) => s + i.summary.itemTotalTax, 0);
  const totalDiscount = data.summary.itemTotalDiscount;
  // itemTotalCashback isn't guaranteed present at item level — confirmed in
  // real payloads it lives reliably at data.summary.itemTotalCashback, so
  // for the multi-metal consolidated total we read it from there directly
  // rather than summing a field that may be 0/undefined per item.
  const totalCashback = data.summary.itemTotalCashback ?? 0;
  const allProductQtyDiscounts = data.items.flatMap((item) =>
  (item.itemBreakDown[0]?.discount ?? []).filter(
    (d: DiscountEntry) => d.issueDiscountType === 'PRODUCT_QTY'
  )
);

  return (
    <div className="oc-wrap" onClick={() => handleModal(false)}>
      <div className="oc-modal" onClick={(e) => e.stopPropagation()}>

        <div className="oc-header">
          <div className="oc-title">
            {isBuyback ? 'Sell Confirmation' : 'Order Confirmation'}
          </div>
          <button className="oc-close" onClick={() => handleModal(false)} aria-label="Close">✕</button>
        </div>

        <div className="oc-summary-bar">
          <div className="oc-sb-left">
            <div className="oc-sb-label">
              {isBuyback ? 'Total you receive' : 'Grand Total'}
            </div>
            <div className="oc-sb-value">₹ {fmt(data.summary.quotationTotal)}</div>
          </div>
          <div className="oc-sb-right">
            <div className="oc-quote-pill">
              <div className="oc-quote-pulse" />
              <ClockIcon />
              <div className="oc-quote-inner">
                <span className="oc-quote-label">Quote expires in</span>
                <span className="oc-quote-time">
                  {minutes > 0 ? `${String(minutes).padStart(2, '0')}m ` : ''}
                  {String(seconds).padStart(2, '0')}s
                </span>
              </div>
            </div>
          </div>
        </div>


        {!isMultiple && (() => {
          const item = data.items[0];
          const bd = item.itemBreakDown[0];
          const meta = METAL_META[bd?.metalCode] ?? { label: bd?.metalCode, colorClass: 'Ag' };
          const rd = bd?.summary?.roundingDifference ?? 0;
          const itemCashback = item.summary.itemTotalCashback ?? data.summary.itemTotalCashback ?? 0;
          const discountInfo = classifyDiscount(bd);

          return (
            <div className="oc-metal-list">
              <div className="oc-metal-card oc-metal-card--flat">

                <div className="oc-mc-header">
                  <div className="oc-mc-left">
                    <div className={`oc-mc-icon ${meta.colorClass}`}>{bd?.metalCode}</div>
                    <div className="oc-mc-info">
                      <div className="oc-mc-name">{meta.label}</div>
                      {/* <div className="oc-mc-subtitle">
                        {bd?.summary?.originalQuantity?.toFixed(4)} g
                        <span className="oc-mc-at">@</span>
                        {fmtCompact(bd?.summary?.unitPrice)}/g
                      </div> */}
                    </div>
                  </div>
                  <div className="oc-mc-right">
                    <div className="oc-mc-total-val">₹ {fmt(item.summary.itemTotalAmount)}</div>
                  </div>
                </div>

                <div className="oc-mc-rows oc-mc-rows--flat">

                  <div className="oc-mc-row">
                    <span className="oc-rl">Rate</span>
                    <span className="oc-rv">{fmtCompact(bd?.summary?.unitPrice)}/g</span>
                  </div>

                  <div className="oc-mc-row">
                    <span className="oc-rl">Quantity</span>
                    <span className="oc-rv">{bd?.summary?.originalQuantity?.toFixed(4)} g</span>
                  </div>

                  <div className="oc-mc-row">
                    <span className="oc-rl">Line amount</span>
                    <span className="oc-rv">₹ {fmt(item.summary.totalLineAmount)}</span>
                  </div>

                  {item.summary.itemTotalDiscount > 0 && (
                    <div className="oc-mc-row">
                      <span className="oc-rl">Discount</span>
                      <span className="oc-rv disc">₹ {fmt(item.summary.itemTotalDiscount)}</span>
                    </div>
                  )}

                  {itemCashback > 0 && (
                    <div className="oc-mc-row">
                      <span className="oc-rl">Cashback</span>
                      <span className="oc-rv disc">₹ {fmt(itemCashback)}</span>
                    </div>
                  )}

                  {discountInfo?.kind === 'PRODUCT_QTY' && (
                    <div className="oc-mc-row">
                      <span className="oc-rl">You get</span>
                      <span className="oc-rv disc">
                        +{Number(discountInfo.entry.value ?? 0).toFixed(4)} g of {discountInfo.entry.DiscountMetal}
                      </span>
                    </div>
                  )}

                  {item.summary.itemTotalTax > 0 && (
                    <div className="oc-mc-row">
                      <span className="oc-rl">GST</span>
                      <span className="oc-rv tax">₹ {fmt(item.summary.itemTotalTax)}</span>
                    </div>
                  )}

                  {rd !== 0 && (
                    <div className="oc-mc-row">
                      <span className="oc-rl">Rounding difference</span>
                      <span className="oc-rv rd">
                        {rd > 0 ? `₹ ${fmt(rd)}` : `₹ ${fmt(Math.abs(rd))}`}
                      </span>
                    </div>
                  )}

                  {!!OrderPayMethod?.length && (
                    <>
                      <div className="oc-pay-section-label">Payment via</div>
                      {OrderPayMethod.map((pm, i) => (
                        <div className="oc-mc-row oc-mc-total-row" key={i}>
                          <span className="oc-rl-label">{pm.paymentMethod}</span>
                          <span className="oc-rv-total">₹ {fmt(pm.amount)}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {isMultiple && (
          <>
            <div className="oc-metal-list">
              {data.items.map((item, idx) => {
                const bd = item.itemBreakDown[0];
                const meta = METAL_META[bd?.metalCode] ?? { label: bd?.metalCode, colorClass: 'Ag' };
                const rd = bd?.summary?.roundingDifference ?? 0;
                const isExpanded = expandedCards[idx] ?? false;
                const itemCashback = item.summary.itemTotalCashback ?? 0;
                const discountInfo = classifyDiscount(bd);

                return (
                  <div className="oc-metal-card" key={idx}>
                    <div
                      className="oc-mc-header oc-mc-collapsible-header"
                      onClick={() => toggleCard(idx)}
                    >
                      <div className="oc-mc-left">
                        <div className={`oc-mc-icon ${meta.colorClass}`}>{bd?.metalCode}</div>
                        <div className="oc-mc-info">
                          <div className="oc-mc-name">{meta.label}</div>
                          <div className="oc-mc-subtitle">
                            {bd?.summary?.originalQuantity?.toFixed(4)} g
                            <span className="oc-mc-at">@</span>
                            {fmtCompact(bd?.summary?.unitPrice)}/g
                          </div>
                        </div>
                      </div>
                      <div className="oc-mc-right">
                        <div className="oc-mc-total-val">₹ {fmt(item.summary.itemTotalAmount)}</div>
                        <div className={`oc-mc-chevron ${isExpanded ? 'open' : ''}`}>
                          {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="oc-mc-rows">
                        <div className="oc-mc-row">
                          <span className="oc-rl">Line amount</span>
                          <span className="oc-rv">₹ {fmt(item.summary.totalLineAmount)}</span>
                        </div>

                        {item.summary.itemTotalDiscount > 0 && (
                          <div className="oc-mc-row">
                            <span className="oc-rl">Discount</span>
                            <span className="oc-rv disc">₹ {fmt(item.summary.itemTotalDiscount)}</span>
                          </div>
                        )}

                        {itemCashback > 0 && (
                          <div className="oc-mc-row">
                            <span className="oc-rl">Cashback</span>
                            <span className="oc-rv disc">₹ {fmt(itemCashback)}</span>
                          </div>
                        )}

                        {discountInfo?.kind === 'PRODUCT_QTY' && (
                          <div className="oc-mc-row">
                            <span className="oc-rl">You get</span>
                            <span className="oc-rv disc">
                              +{Number(discountInfo.entry.value ?? 0).toFixed(4)} g of {discountInfo.entry.DiscountMetal}
                            </span>
                          </div>
                        )}

                        {item.summary.itemTotalTax > 0 && (
                          <div className="oc-mc-row">
                            <span className="oc-rl">GST</span>
                            <span className="oc-rv tax">₹ {fmt(item.summary.itemTotalTax)}</span>
                          </div>
                        )}

                        {rd !== 0 && (
                          <div className="oc-mc-row">
                            <span className="oc-rl">Rounding difference</span>
                            <span className="oc-rv rd">
                              {rd > 0 ? `₹ ${fmt(rd)}` : `₹ ${fmt(Math.abs(rd))}`}
                            </span>
                          </div>
                        )}

                        <div className="oc-mc-row oc-mc-total-row">
                          <span className="oc-rl-total">Item total</span>
                          <span className="oc-rv-total">₹ {fmt(item.summary.itemTotalAmount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="oc-consolidated">
              <div className="oc-consolidated-title">Order Summary</div>

              <div className="oc-consolidated-row">
                <span className="oc-cl">Line amount</span>
                <span className="oc-cv">₹ {fmt(totalLineAmount)}</span>
              </div>

              {totalDiscount > 0 && (
                <div className="oc-consolidated-row">
                  <span className="oc-cl">Total discount</span>
                  <span className="oc-cv disc">₹ {fmt(totalDiscount)}</span>
                </div>
              )}

              {totalCashback > 0 && (
                <div className="oc-consolidated-row">
                  <span className="oc-cl">Total cashback</span>
                  <span className="oc-cv disc">₹ {fmt(totalCashback)}</span>
                </div>
              )}

              {allProductQtyDiscounts.map((d, i) => (
                <div className="oc-consolidated-row" key={i}>
                  <span className="oc-cl disc">You get</span>
                  <span className="oc-cv disc">
                    +{Number(d.value).toFixed(4)} g of {d.DiscountMetal}
                  </span>
                </div>
              ))}

              {totalTax > 0 && (
                <div className="oc-consolidated-row">
                  <span className="oc-cl">Total GST</span>
                  <span className="oc-cv tax">₹ {fmt(totalTax)}</span>
                </div>
              )}

              {data.summary.quotationRoundingDifference !== 0 && (
                <div className="oc-consolidated-row">
                  <span className="oc-cl">Rounding</span>
                  <span className="oc-cv rd">
                    {data.summary.quotationRoundingDifference > 0
                      ? `₹ ${fmt(data.summary.quotationRoundingDifference)}`
                      : `₹ ${fmt(Math.abs(data.summary.quotationRoundingDifference))}`}
                  </span>
                </div>
              )}

              {!!OrderPayMethod?.length && (
                <>
                  <div className="oc-pay-title">Payment via</div>
                  {OrderPayMethod.map((pm, i) => (
                    <div className="oc-consolidated-row oc-pay-row" key={i}>
                      <span className="oc-cl oc-pay-label">{pm.paymentMethod}</span>
                      <span className="oc-cv oc-pay-val">₹ {fmt(pm.amount)}</span>
                    </div>
                  ))}
                </>
              )}

              <div className="oc-consolidated-total-row">
                <span className="oc-ct-label">Grand Total</span>
                <span className="oc-ct-value">₹ {fmt(data.summary.quotationTotal)}</span>
              </div>
            </div>
          </>
        )}

        <div className="oc-dashed-divider" />

        {!isBuyback && (
          <div className="oc-disclaimer">
            <InfoIcon />
            <span>
              The ownership of your metal will be confirmed with a GST tax invoice over email.
            </span>
          </div>
        )}

        <div className="oc-cta">
          <button
            className={`oc-cta-btn ${isBuyback ? 'buyback' : 'sell'}`}
            onClick={onProceedPayment}
          >
            {isBuyback ? 'Confirm Sell →' : 'Make payment →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(OrderConfirmation, (prev, next) => {

  if (!prev.isShow && !next.isShow) return true;
  return (
    prev.isShow === next.isShow &&
    prev.data === next.data &&
    prev.minutes === next.minutes &&
    prev.seconds === next.seconds &&
    prev.onProceedPayment === next.onProceedPayment &&
    prev.OrderPayMethod === next.OrderPayMethod
  );
});